import { useCustomSource } from '@uofu-uss-gis/ui-react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { createParkingLotPopupTemplate } from '../utils/parkingLotPopupTemplate';
import { buildSurfaceLotsUrl } from '../config/eventParkingConfig';

// Create the parking FeatureLayer for querying
const parkingFL = new FeatureLayer({
  url: buildSurfaceLotsUrl(),
  popupTemplate: createParkingLotPopupTemplate(),
});

const escapeSqlLike = (value: string): string => {
  return value.replace(/'/g, "''");
};

type SortableSuggestion = {
  text: string;
  key: string;
  sourceIndex: number;
  source: {
    lotNumber?: string;
  };
};

const extractSortableLotValue = (suggestion: SortableSuggestion): number => {
  const sourceLot = suggestion.source?.lotNumber ?? '';
  const textFallback = suggestion.text;
  const match = (sourceLot || textFallback).match(/\d+/);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }
  const parsed = parseInt(match[0], 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

/**
 * Search source for parking lots
 * Allows users to search for parking by lot name or number
 */
const parkingSearchSource = {
  sourceIndex: 7, // After built-in sources (0-6)
  name: 'Campus Lots',
  placeholder: 'Search parking lots',
  maxResults: 100,
  maxSuggestions: 100,
  minSuggestCharacters: 1,
  // Reference to the actual map layer - SearchWidget will use this instead of getResults
  layerId: 'parkingLotsLayer',
  sortSuggestions: (suggestions: SortableSuggestion[]) => {
    return [...suggestions].sort((a, b) => extractSortableLotValue(a) - extractSortableLotValue(b));
  },
  
  getSuggestions: async ({ suggestTerm }: { suggestTerm: string }) => {
    const term = suggestTerm.trim().toUpperCase();
    if (!term) return [];
    
    const escaped = escapeSqlLike(term);
    
    // Query parking lots by lot name or number (matching archive logic)
    const query = {
      where: `UPPER(LOTNAME) LIKE '%${escaped}%' OR UPPER(LOTNUMBER) LIKE '%${escaped}%'`,
      outFields: ['OBJECTID', 'LOTNAME', 'LOTNUMBER'],
      returnGeometry: false,
      num: 10,
    };
    
    const results = await parkingFL.queryFeatures(query);
    
    return (results.features || []).map((f) => {
      const attrs = f.attributes;
      const lotName = attrs.LOTNAME as string;
      const lotNumber = attrs.LOTNUMBER as string;
      
      return {
        text: `${lotName} - ${lotNumber}`,
        key: String(attrs.OBJECTID),
        magicKey: String(attrs.OBJECTID),
        lotNumber,
      };
    });
  },
  
  // Note: getResults is intentionally omitted - SearchWidget will use featureLayer instead
};

/**
 * Component that registers parking lots as a searchable source
 * Uses priority: 0 to place parking lots at the top of search filters (index 0)
 */
export function ParkingSearchSource() {
  useCustomSource(parkingSearchSource, { priority: 0 });
  return null;
}


export default ParkingSearchSource;
